"use client";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tag,
  Text,
  TextLabel,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@focal/ui-kit";

import {
  ContactPhoneType,
  CONTACT_PHONE_TYPE_LABELS,
} from "@/types";
import { formatPhoneNumber, removeNonNumeric } from "@/lib";

/** Maximum number of phone numbers a client may carry (enforced client-side). */
export const MAX_PHONES = 5;

const PRIMARY_PHONE_TOOLTIP =
  "This is the primary phone number that will be used by Focal for identifying this client on incoming and outgoing calls.";

/**
 * A locally-staged phone row. `value` is the formatted display string; `key` is
 * a client-side identifier only (these rows do not exist on the backend yet).
 */
export interface PhoneDraft {
  key: string;
  value: string;
  type: ContactPhoneType;
  primary: boolean;
}

/** Create an empty draft row. The first row in a list is primary. */
export const newPhoneDraft = (primary: boolean): PhoneDraft => ({
  key: crypto.randomUUID(),
  value: "",
  type: ContactPhoneType.HOME,
  primary,
});

/**
 * True when no row carries an invalid number. Empty rows are ignored (phones
 * are optional); any present row must resolve to exactly 10 digits.
 */
export const arePhoneDraftsValid = (drafts: PhoneDraft[]): boolean =>
  drafts.every((d) => {
    const digits = removeNonNumeric(d.value);
    return digits.length === 0 || digits.length === 10;
  });

/** True when at least one row holds a complete 10-digit number. */
export const hasValidPhone = (drafts: PhoneDraft[]): boolean =>
  drafts.some((d) => removeNonNumeric(d.value).length === 10);

interface PhoneDraftListProps {
  value: PhoneDraft[];
  onChange: (next: PhoneDraft[]) => void;
}

/**
 * Create-flow counterpart to {@link ClientPhoneList}: an always-editable,
 * fully-local list of phone drafts, each with a Work/Home/Mobile type. Nothing
 * is persisted here — the parent modal reads the drafts and sends them as the
 * `phoneNumbers` array in the single create request. Visuals mirror the edit
 * list (type select, primary tag, per-row 3-dot menu, "Add phone" affordance,
 * max-5 guard).
 *
 * Phones are optional, so the list may be empty; when any rows exist, exactly
 * one is primary.
 */
export const PhoneDraftList = ({ value, onChange }: PhoneDraftListProps) => {
  const setValue = (key: string, raw: string) =>
    onChange(
      value.map((d) =>
        d.key === key
          ? {
              ...d,
              value: formatPhoneNumber(removeNonNumeric(raw).slice(0, 10)),
            }
          : d,
      ),
    );

  const setType = (key: string, type: ContactPhoneType) =>
    onChange(value.map((d) => (d.key === key ? { ...d, type } : d)));

  const makePrimary = (key: string) =>
    onChange(value.map((d) => ({ ...d, primary: d.key === key })));

  const addRow = () =>
    onChange([...value, newPhoneDraft(value.length === 0)]);

  const removeRow = (key: string) => {
    const next = value.filter((d) => d.key !== key);
    // Promote a remaining row to primary if we removed the primary one.
    if (next.length > 0 && !next.some((d) => d.primary)) {
      next[0] = { ...next[0], primary: true };
    }
    onChange(next);
  };

  const rowError = (draft: PhoneDraft): string | null => {
    const digits = removeNonNumeric(draft.value);
    if (digits.length === 0) return null;
    return digits.length === 10 ? null : "Enter a valid 10-digit phone number";
  };

  const renderTypeSelect = (
    selected: ContactPhoneType,
    onValueChange: (next: ContactPhoneType) => void,
  ) => (
    <Select
      value={selected}
      onValueChange={(next) => onValueChange(next as ContactPhoneType)}
    >
      <SelectTrigger
        className="w-[110px] shrink-0"
        data-cy="client-phone-draft-type"
        aria-label="Phone type"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.values(ContactPhoneType).map((type) => (
          <SelectItem key={type} value={type}>
            {CONTACT_PHONE_TYPE_LABELS[type]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderPrimaryTag = () => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">
          <Tag>Primary</Tag>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{PRIMARY_PHONE_TOOLTIP}</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="flex flex-1 flex-col gap-2 max-w-[360px] min-w-[250px]">
      <TextLabel>Phone</TextLabel>

      <div className="flex flex-col gap-2">
        {value.map((draft) => {
          const error = rowError(draft);
          return (
            <div
              key={draft.key}
              data-cy="client-phone-draft-row"
              className="flex items-center gap-2"
            >
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Input
                    type="tel"
                    value={draft.value}
                    data-cy="client-phone-draft-input"
                    onChange={(event) => setValue(draft.key, event.target.value)}
                    placeholder="(555) 555-5555"
                  />
                  {renderTypeSelect(draft.type, (next) =>
                    setType(draft.key, next),
                  )}
                </div>
                {error && (
                  <Text variant="p3" textColor="error">
                    {error}
                  </Text>
                )}
              </div>

              {draft.primary ? (
                renderPrimaryTag()
              ) : (
                // Reserve the primary-pill slot so every input stops at the
                // same right edge instead of growing into the empty space.
                <span aria-hidden className="invisible inline-flex shrink-0">
                  <Tag>Primary</Tag>
                </span>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="iconSm"
                    variant="ghostSecondary"
                    type="button"
                    aria-label="Phone options"
                    data-cy="client-phone-draft-menu"
                  >
                    <Icon name="MoreVertIcon" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!draft.primary && (
                    <DropdownMenuItem
                      data-cy="client-phone-draft-make-primary"
                      onClick={() => makePrimary(draft.key)}
                    >
                      Make primary
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    data-cy="client-phone-draft-remove"
                    onClick={() => removeRow(draft.key)}
                  >
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>

      {value.length >= MAX_PHONES ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex w-fit">
              <Button
                size="sm"
                variant="link"
                type="button"
                data-cy="client-phone-draft-add"
                disabled
                iconLeft={<Icon name="AddIcon" />}
              >
                Add phone
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            You can add up to {MAX_PHONES} phone numbers.
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button
          size="sm"
          variant="link"
          type="button"
          className="w-fit"
          data-cy="client-phone-draft-add"
          iconLeft={<Icon name="AddIcon" />}
          onClick={addRow}
        >
          Add phone
        </Button>
      )}
    </div>
  );
};
